import { FilterMenu, type FilterOption } from '@/components/FilterMenu'
import { InputFilterMenu } from '@/components/FilterMenu/InputFilterMenu'
import type {
  AuditLogStatus,
  DateRangePreset,
} from '@/features/AuditLogs/hooks/useAuditLogFilters'
import { Button, Flex, HStack, Icon, Tag } from '@chakra-ui/react'
import { X } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface AuditLogsFiltersProps {
  dateRangePreset: DateRangePreset
  hiveId: string | null
  orgId: string | null
  user: string | null
  status: AuditLogStatus | null
  hasActiveFilters: boolean
  onDateRangeChange: (preset: DateRangePreset) => void
  onHiveIdChange: (value: string | null) => void
  onOrgIdChange: (value: string | null) => void
  onUserChange: (value: string | null) => void
  onStatusChange: (value: AuditLogStatus | null) => void
  onClearFilters: () => void
}

export function AuditLogsFilters({
  dateRangePreset,
  hiveId,
  orgId,
  user,
  status,
  hasActiveFilters,
  onDateRangeChange,
  onHiveIdChange,
  onOrgIdChange,
  onUserChange,
  onStatusChange,
  onClearFilters,
}: Readonly<AuditLogsFiltersProps>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const { t: tTranslation } = useTranslation('translation', {
    useSuspense: false,
  })

  // Date range options
  const dateRangeOptions: FilterOption[] = useMemo(
    () => [
      {
        value: 'lastHour',
        label: String(t('auditLogs.filters.presets.lastHour')),
      },
      {
        value: 'last24Hours',
        label: String(t('auditLogs.filters.presets.last24Hours')),
      },
      {
        value: 'last7Days',
        label: String(t('auditLogs.filters.presets.last7Days')),
      },
      {
        value: 'last30Days',
        label: String(t('auditLogs.filters.presets.last30Days')),
      },
    ],
    [t],
  )

  // Status options - lowercase values to match API
  const statusOptions: FilterOption[] = useMemo(
    () => [
      { value: 'success', label: String(t('auditLogs.status.success')) },
      { value: 'error', label: String(t('auditLogs.status.error')) },
    ],
    [t],
  )

  // Get the selected option objects
  const selectedDateRange = dateRangeOptions.find(
    (opt) => opt.value === dateRangePreset,
  )
  const selectedStatus = status
    ? statusOptions.find((opt) => opt.value === status)
    : null

  const dateRangeLabel = String(t('auditLogs.filters.dateRange'))
  const hiveIdLabel = String(t('auditLogs.filters.hiveId'))
  const orgIdLabel = String(t('auditLogs.filters.orgId'))
  const userLabel = String(t('auditLogs.filters.user'))
  const statusLabel = String(t('auditLogs.filters.status'))

  return (
    <Flex direction="column" gap={3}>
      {/* Filter Controls */}
      <HStack gap={2} wrap="wrap">
        {/* Date Range Filter */}
        <FilterMenu
          buttonLabel={dateRangeLabel}
          options={dateRangeOptions}
          selectedOption={selectedDateRange ?? null}
          onOptionChange={(option) =>
            onDateRangeChange(
              (option?.value as DateRangePreset) ?? 'last24Hours',
            )
          }
        />

        {/* Hive ID Filter */}
        <InputFilterMenu
          buttonLabel={hiveIdLabel}
          placeholder={hiveIdLabel}
          selectedValue={hiveId}
          onSearch={(value) => onHiveIdChange(value || null)}
        />

        {/* Organization ID Filter */}
        <InputFilterMenu
          buttonLabel={orgIdLabel}
          placeholder={orgIdLabel}
          selectedValue={orgId}
          onSearch={(value) => onOrgIdChange(value || null)}
        />

        {/* User Filter */}
        <InputFilterMenu
          buttonLabel={userLabel}
          placeholder={userLabel}
          selectedValue={user}
          onSearch={(value) => onUserChange(value || null)}
        />

        {/* Status Filter */}
        <FilterMenu
          buttonLabel={statusLabel}
          options={statusOptions}
          selectedOption={selectedStatus ?? null}
          onOptionChange={(option) =>
            onStatusChange((option?.value as AuditLogStatus) ?? null)
          }
        />

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            colorPalette="gray"
          >
            <Icon as={X} />
            {tTranslation('components.filterMenu.clearAllFilters')}
          </Button>
        )}
      </HStack>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <HStack gap={2} wrap="wrap">
          {dateRangePreset !== 'last24Hours' && selectedDateRange && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {dateRangeLabel}: {selectedDateRange.label}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-date-range-tag"
                  aria-label={`Remove ${dateRangeLabel} filter`}
                  cursor="pointer"
                  onClick={() => onDateRangeChange('last24Hours')}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {hiveId && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {hiveIdLabel}: {hiveId}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-hive-id-tag"
                  aria-label={`Remove ${hiveIdLabel} filter`}
                  cursor="pointer"
                  onClick={() => onHiveIdChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {orgId && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {orgIdLabel}: {orgId}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-org-id-tag"
                  aria-label={`Remove ${orgIdLabel} filter`}
                  cursor="pointer"
                  onClick={() => onOrgIdChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {user && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {userLabel}: {user}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-user-tag"
                  aria-label={`Remove ${userLabel} filter`}
                  cursor="pointer"
                  onClick={() => onUserChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}

          {status && selectedStatus && (
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {statusLabel}: {selectedStatus.label}
              </Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger
                  data-testid="close-status-tag"
                  aria-label={`Remove ${statusLabel} filter`}
                  cursor="pointer"
                  onClick={() => onStatusChange(null)}
                />
              </Tag.EndElement>
            </Tag.Root>
          )}
        </HStack>
      )}
    </Flex>
  )
}
